import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import {
  ToggleLeft,
  Plug,
  Lightbulb,
  Fan,
  CircuitBoard,
  ShieldCheck,
  Flame,
  Zap,
} from "lucide-react";
import { waLink } from "@/lib/config";

const products = [
  {
    title: "Modular Switches",
    desc: "Elegant, safe & durable switch plates in modern designs.",
    Icon: ToggleLeft,
    image:
      "https://customer-assets.emergentagent.com/job_mastermall-showroom/artifacts/rium3kbo_WhatsApp%20Image%202026-07-02%20at%2012.08.26%20AM.jpeg",
  },
  {
    title: "Wires & Cables",
    desc: "ISI-marked wires & cables from India's most trusted brands.",
    Icon: Plug,
    image:
      "https://customer-assets.emergentagent.com/job_mastermall-showroom/artifacts/uhq0eo32_WhatsApp%20Image%202026-07-02%20at%2012.08.29%20AM.jpeg",
  },
  {
    title: "LED Lighting",
    desc: "Energy-efficient panels, downlights, decorative & smart lights.",
    Icon: Lightbulb,
    image:
      "https://customer-assets.emergentagent.com/job_mastermall-showroom/artifacts/xwu86udz_WhatsApp%20Image%202026-07-02%20at%2012.08.28%20AM.jpeg",
  },
  {
    title: "Fans",
    desc: "Ceiling, wall, exhaust & designer fans for every space.",
    Icon: Fan,
    image:
      "https://customer-assets.emergentagent.com/job_mastermall-showroom/artifacts/5tbwjs54_WhatsApp%20Image%202026-07-02%20at%2012.08.25%20AM.jpeg",
  },
  {
    title: "PVC Conduits",
    desc: "Premium concealed & surface PVC conduits and accessories.",
    Icon: CircuitBoard,
    image:
      "https://customer-assets.emergentagent.com/job_mastermall-showroom/artifacts/y0vylol6_WhatsApp%20Image%202026-07-02%20at%2012.08.29%20AM%20%282%29.jpeg",
  },
  {
    title: "Switchgear",
    desc: "MCBs, RCCBs, distribution boards & industrial switchgear.",
    Icon: ShieldCheck,
  },
  {
    title: "Geysers",
    desc: "Instant & storage water heaters from top-rated brands.",
    Icon: Flame,
  },
  {
    title: "Electrical Accessories",
    desc: "Sockets, plugs, holders, extensions & wiring accessories.",
    Icon: Zap,
    image:
      "https://customer-assets.emergentagent.com/job_mastermall-showroom/artifacts/vzrjyhvl_WhatsApp%20Image%202026-07-02%20at%2012.08.27%20AM.jpeg",
  },
];

export default function Products() {
  return (
    <section
      id="products"
      data-testid="products-section"
      className="py-16 md:py-24 bg-[#F8F9FA] relative"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#334155] font-semibold mb-4">
              — Our Products
            </div>
            <h2
              className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-[#00296B] leading-tight mm-underline max-w-2xl"
              data-testid="products-heading"
            >
              Everything electrical, all in one place
            </h2>
          </div>
          <p className="text-[#334155] text-base sm:text-lg max-w-md">
            Explore our extensive catalogue across 8 essential categories —
            handpicked from India&apos;s most trusted brands.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {products.map((p, i) => (
            <motion.a
              key={p.title}
              href={waLink(
                `Hi Master Mall, I'm interested in your ${p.title}. Please share details.`
              )}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`product-card-${p.title.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="mm-card group relative bg-white border border-[#00296B]/10 flex flex-col justify-between min-h-[280px] overflow-hidden"
            >
              {p.image ? (
                <div className="relative h-40 w-full overflow-hidden bg-[#F1F3F5]">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute top-3 left-3 w-10 h-10 bg-white/90 backdrop-blur flex items-center justify-center">
                    <p.Icon className="w-5 h-5 text-[#00296B]" strokeWidth={1.6} />
                  </div>
                </div>
              ) : (
                <div className="p-7 pb-0">
                  <div className="w-14 h-14 bg-[#00296B]/5 flex items-center justify-center group-hover:bg-[#FFD500] transition-colors">
                    <p.Icon
                      className="w-7 h-7 text-[#00296B]"
                      strokeWidth={1.6}
                    />
                  </div>
                </div>
              )}
              <div className="p-7 pt-5 flex-1 flex flex-col justify-between">
                <div>
                  <h3 className="font-display font-semibold text-lg text-[#00296B] leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm text-[#334155] leading-relaxed">
                    {p.desc}
                  </p>
                </div>
                <div className="mt-5 flex items-center justify-between text-[#00296B] text-sm font-semibold">
                  <span className="group-hover:underline">Enquire</span>
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </div>
              </div>

              {/* corner accent */}
              <span className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-[#FFD500] opacity-0 group-hover:opacity-100 transition-opacity z-10" />
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
